/**
 * Route Gravity Forms popup submissions to the clicked team member's email.
 *
 * The email itself is not visible on the page. The script walks up the DOM
 * from the clicked trigger link until it finds an ancestor whose text
 * content contains an email address, up to a safety depth limit, and stores
 * it in a hidden Gravity Forms field. The routing decision itself must be
 * validated server-side against a whitelist before being used as the
 * notification recipient.
 */

const EMAIL_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;
const HIDDEN_FIELD_SELECTOR = '#input_5_9';
const TRIGGER_SELECTOR = 'a[href="#send-message"]';
const MAX_ANCESTOR_DEPTH = 6;

/**
 * Find the nearest ancestor of an element whose text content contains an email address.
 *
 * @param element - Element to start searching from.
 * @return The matched email address, or null if none was found within the depth limit.
 */
function findEmailInAncestors( element: Element ): string | null {
	let node: Element | null = element;

	for ( let depth = 0; depth < MAX_ANCESTOR_DEPTH && node; depth++ ) {
		const match = node.textContent?.match( EMAIL_PATTERN );

		if ( match ) {
			return match[ 0 ];
		}

		node = node.parentElement;
	}

	return null;
}

/**
 * Handle a click anywhere on the document and, if it originated from a
 * popup trigger link, fill the hidden Gravity Forms field with the
 * corresponding team member's email address.
 *
 * @param event - The click event.
 */
function handleTriggerClick( event: MouseEvent ): void {
	const target = event.target as Element | null;
	const trigger = target?.closest( TRIGGER_SELECTOR );

	if ( ! trigger ) {
		return;
	}

	const hiddenField = document.querySelector< HTMLInputElement >(
		HIDDEN_FIELD_SELECTOR
	);

	if ( ! hiddenField ) {
		return;
	}

	const email = findEmailInAncestors( trigger );

	if ( email ) {
		hiddenField.value = email;
	}
}

document.addEventListener( 'click', handleTriggerClick );
